import { InjectionToken, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformServer } from '@angular/common';

import { environment } from '../../../environments/environment';

export interface SavingArenaApiConfig {
  apiBaseUrl: string;
  assetBaseUrl: string;
  /** Absolute public origin (no trailing slash) — used for canonical / OG URLs. */
  siteOrigin: string;
}

// Reads an env var on the SSR Node process. Returns '' in the browser (where `process` doesn't
// exist), so browser code always falls back to the compiled-in environment values.
function serverEnv(name: string): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const proc = (globalThis as any)['process'];
  const value = proc?.env?.[name];
  return typeof value === 'string' ? value : '';
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

/**
 * Builds the API config for the platform currently rendering.
 *
 * The browser and the SSR server do NOT talk to the backend the same way:
 *
 * - In the browser, requests go to the public origin (`apiBaseUrl`) so they pass through nginx/CDN
 *   exactly like any other client request.
 * - During server rendering the Node process is usually on the same host as the backend, so it uses
 *   `serverApiBaseUrl` (an internal address, e.g. 127.0.0.1) instead of looping back out through the
 *   public hostname. That is faster and keeps SSR working if public DNS/TLS is degraded.
 *
 * Note SSR requires ABSOLUTE URLs — there is no document base to resolve a relative path against on
 * the server — which is why every value here is absolute.
 *
 * Env vars let one build artifact be promoted dev -> staging -> production without a rebuild
 * (spec §13): SSR_API_BASE_URL, PUBLIC_API_BASE_URL, PUBLIC_ASSET_BASE_URL, PUBLIC_SITE_ORIGIN.
 */
export function createSavingArenaApiConfig(isServer: boolean): SavingArenaApiConfig {
  const publicApiBaseUrl = serverEnv('PUBLIC_API_BASE_URL') || environment.apiBaseUrl;
  const internalApiBaseUrl =
    serverEnv('SSR_API_BASE_URL') || environment.serverApiBaseUrl || publicApiBaseUrl;

  return {
    apiBaseUrl: stripTrailingSlash(isServer ? internalApiBaseUrl : publicApiBaseUrl),
    assetBaseUrl: stripTrailingSlash(
      serverEnv('PUBLIC_ASSET_BASE_URL') || environment.assetBaseUrl,
    ),
    siteOrigin: stripTrailingSlash(serverEnv('PUBLIC_SITE_ORIGIN') || environment.siteOrigin),
  };
}

export const SAVING_ARENA_API_CONFIG = new InjectionToken<SavingArenaApiConfig>(
  'SavingArena API config',
  {
    providedIn: 'root',
    factory: () => createSavingArenaApiConfig(isPlatformServer(inject(PLATFORM_ID))),
  },
);

/**
 * Browser-shaped defaults, kept for tests and for any caller that overrides the token directly.
 * Prefer injecting SAVING_ARENA_API_CONFIG — it resolves the correct URLs per platform.
 */
export const DEFAULT_SAVING_ARENA_API_CONFIG: SavingArenaApiConfig =
  createSavingArenaApiConfig(false);
